import React, { useMemo } from 'react';

import { shallowCompare } from '@grafana/data';
import { t, Trans } from '@grafana/i18n';
import { SceneComponentProps, sceneGraph, SceneObjectBase, SceneObjectState } from '@grafana/scenes';
import { Button, Tooltip } from '@grafana/ui';

import { reportAppInteraction, USER_EVENTS_ACTIONS, USER_EVENTS_PAGES } from '../../services/analytics';
import { getNormalizedFieldName, LOG_LINE_BODY_FIELD_NAME } from '../../services/logFieldNames';
import { LogsListScene } from './LogsListScene';
import { ServiceScene } from './ServiceScene';

interface ShowDefaultFieldsButtonSceneState extends SceneObjectState {}
export class LogOptionsButtonsScene extends SceneObjectBase<ShowDefaultFieldsButtonSceneState> {
  static Component = ShowDefaultFieldsButtonRenderer;

  getLogsListScene = () => {
    return sceneGraph.getAncestor(this, LogsListScene);
  };

  showBackendFields = () => {
    const parentScene = this.getLogsListScene();
    parentScene.showBackendFields();
    reportAppInteraction(
      USER_EVENTS_PAGES.service_details,
      USER_EVENTS_ACTIONS.service_details.logs_show_backend_fields
    );
  };

  clearDisplayedFields = () => {
    const parentScene = this.getLogsListScene();
    parentScene.clearDisplayedFields();
    reportAppInteraction(
      USER_EVENTS_PAGES.service_details,
      USER_EVENTS_ACTIONS.service_details.logs_clear_displayed_fields
    );
  };
}

function ShowDefaultFieldsButtonRenderer({ model }: SceneComponentProps<LogOptionsButtonsScene>) {
  const serviceScene = sceneGraph.getAncestor(model, ServiceScene);
  const { backendDisplayedFields } = serviceScene.useState();
  const { displayedFields, otelDisplayedFields } = model.getLogsListScene().useState();

  const displayedFieldsNames = useMemo(() => displayedFields.map(getNormalizedFieldName).join(', '), [displayedFields]);
  const backendFieldsNames = useMemo(
    () => backendDisplayedFields?.map(getNormalizedFieldName).join(', '),
    [backendDisplayedFields]
  );

  const hasDisplayedFields = displayedFields.length > 0;
  const hasBackendDisplayedFields = backendDisplayedFields && backendDisplayedFields?.length > 0;
  const displayedFieldsIsOnlyLogLine =
    !hasDisplayedFields || (displayedFields.length === 1 && displayedFields[0] === LOG_LINE_BODY_FIELD_NAME);

  return (
    <>
      {!displayedFieldsIsOnlyLogLine && hasDisplayedFields && !shallowCompare(displayedFields, otelDisplayedFields) && (
        <Tooltip
          content={t(
            'components.service-scene.log-options-buttons-scene.tooltip.clear-displayed-fields',
            'Clear displayed fields: {{fields}}',
            { fields: displayedFieldsNames }
          )}
        >
          <Button size={'sm'} variant="secondary" fill="outline" onClick={model.clearDisplayedFields}>
            <Trans i18nKey="components.service-scene.log-options-buttons-scene.show-original-log-line">
              Show original log line
            </Trans>
          </Button>
        </Tooltip>
      )}
      {hasBackendDisplayedFields && !shallowCompare(displayedFields, backendDisplayedFields) && (
        <Tooltip
          content={t('components.service-scene.log-options-buttons-scene.tooltip.show-default-fields', 'Show default fields: {{fields}}', {
            fields: backendFieldsNames,
          })}
        >
          <Button size={'sm'} variant="secondary" fill="outline" onClick={model.showBackendFields}>
            <Trans i18nKey="components.service-scene.log-options-buttons-scene.show-default-fields">
              Show default fields
            </Trans>
          </Button>
        </Tooltip>
      )}
    </>
  );
}
